import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { AgentItem, CreateAgentInput, CreateDomainInput, DomainItem, Paginated, SendEmailInput, ThreadDetail, ThreadItem } from '@crm/shared';
import { api } from '../../api/client';

export const useDomains = () => useQuery({ queryKey: ['domains'], queryFn: async () => (await api.get<DomainItem[]>('/domains')).data });
export const useAgents = () => useQuery({ queryKey: ['agents'], queryFn: async () => (await api.get<AgentItem[]>('/agents')).data });
export const useMergeTags = () =>
  useQuery({ queryKey: ['merge-tags'], queryFn: async () => (await api.get<{ tag: string; label: string }[]>('/emails/merge-tags')).data, staleTime: 5 * 60_000 });

function useInvalidate(keys: string[][]) {
  const qc = useQueryClient();
  return () => keys.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}

export function useCreateDomain() {
  const inv = useInvalidate([['domains']]);
  return useMutation({ mutationFn: async (input: CreateDomainInput) => (await api.post<DomainItem>('/domains', input)).data, onSuccess: inv });
}
export function useUpdateDomain() {
  const inv = useInvalidate([['domains'], ['agents']]);
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string; inboundEnabled?: boolean; verified?: boolean }) => (await api.patch<DomainItem>(`/domains/${id}`, input)).data,
    onSuccess: inv,
  });
}
export function useDeleteDomain() {
  const inv = useInvalidate([['domains']]);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/domains/${id}`)).data, onSuccess: inv });
}
export function useSyncDomains() {
  const inv = useInvalidate([['domains']]);
  return useMutation({ mutationFn: async () => (await api.post<{ created: number; updated: number; total: number }>('/domains/sync')).data, onSuccess: inv });
}

export function useCreateAgent() {
  const inv = useInvalidate([['agents'], ['domains']]);
  return useMutation({ mutationFn: async (input: CreateAgentInput) => (await api.post<AgentItem>('/agents', input)).data, onSuccess: inv });
}
export function useUpdateAgent() {
  const inv = useInvalidate([['agents'], ['domains']]);
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<CreateAgentInput> & { id: string }) => (await api.patch<AgentItem>(`/agents/${id}`, input)).data,
    onSuccess: inv,
  });
}
export function useDeleteAgent() {
  const inv = useInvalidate([['agents'], ['domains']]);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/agents/${id}`)).data, onSuccess: inv });
}

// --- inbox ---------------------------------------------------------------------

export interface ThreadParams {
  domainId?: string;
  contactId?: string;
  status?: 'inbox' | 'unread' | 'archived' | 'all';
  q?: string;
  page?: number;
  pageSize?: number;
}

export function useThreads(params: ThreadParams) {
  return useQuery({
    queryKey: ['threads', params],
    queryFn: async () => (await api.get<Paginated<ThreadItem>>('/inbox/threads', { params })).data,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

export function useThread(id: string | undefined, markRead = true) {
  return useQuery({
    queryKey: ['thread', id],
    queryFn: async () => (await api.get<ThreadDetail>(`/inbox/threads/${id}`, { params: markRead ? { markRead: 1 } : {} })).data,
    enabled: !!id,
  });
}

export function useInboxSummary(enabled = true) {
  return useQuery({
    queryKey: ['inbox-summary'],
    enabled,
    queryFn: async () => (await api.get<{ domains: { id: string; domain: string; unread: number }[]; totalUnread: number }>('/inbox/summary')).data,
    refetchInterval: 30_000,
  });
}

const THREAD_KEYS = [['threads'], ['thread'], ['inbox-summary'], ['activities'], ['contact']];

export function useSendEmail() {
  const inv = useInvalidate(THREAD_KEYS);
  return useMutation({ mutationFn: async (input: SendEmailInput) => (await api.post<{ threadId: string }>('/emails/send', input)).data, onSuccess: inv });
}
export function useReplyThread() {
  const inv = useInvalidate(THREAD_KEYS);
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string; agentId?: string; html: string }) => (await api.post<ThreadDetail>(`/inbox/threads/${id}/reply`, input)).data,
    onSuccess: inv,
  });
}
export function useUpdateThread() {
  const inv = useInvalidate(THREAD_KEYS);
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string; unread?: boolean; archived?: boolean; contactId?: string | null }) => (await api.patch<ThreadDetail>(`/inbox/threads/${id}`, input)).data,
    onSuccess: inv,
  });
}
export function useDeleteThread() {
  const inv = useInvalidate(THREAD_KEYS);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/inbox/threads/${id}`)).data, onSuccess: inv });
}
